import { Suspense, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';

import { LoadingSkeleton } from '../common/components/skeleton/skeleton';
import { ErrorBoundary } from '../common/error-boundary';
import { envType } from '../common/types';
import Query, {
	type authContextProviderQuery,
} from '../controllers/__generated__/authContextProviderQuery.graphql';
import { AuthContextProvider } from '../controllers/auth-context-provider';
import { getEnvType } from '../utils';

import { AppWithTenantContext } from './AppWithTenantContext';

const redirectUrl =
	getEnvType() === envType.PROD
		? `https://id.atlassian.com/login?continue=${encodeURIComponent(window.location.href)}`
		: `https://id.stg.internal.atlassian.com/login?continue=${encodeURIComponent(
				window.location.href,
			)}`;

const AuthErrorBoundary = ({ children }: { children: React.ReactNode }) => {
	return (
		<ErrorBoundary
			fallback={() => {
				// Redirect on auth errors except for localhost
				if (window && window.location.hostname !== 'localhost') {
					window.location.replace(redirectUrl);
				}
				return <LoadingSkeleton />;
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export const AppWithAuthContext = () => {
	const [queryRef, loadQuery] = useQueryLoader<authContextProviderQuery>(Query);

	useEffect(() => {
		loadQuery({});
	}, [loadQuery]);

	return (
		<AuthErrorBoundary>
			<Suspense fallback={<LoadingSkeleton />}>
				{queryRef && (
					<AuthContextProvider queryRef={queryRef}>
						<AppWithTenantContext />
					</AuthContextProvider>
				)}
			</Suspense>
		</AuthErrorBoundary>
	);
};
